/**
 * Da un elenco di reperti a un elenco di **azioni**.
 *
 * ## Perché serve
 *
 * Ottanta righe che dicono «CVE-2022-26353 — qemu-guest-agent» sono un elenco
 * di fatti, e chi le legge deve ancora capire da solo cosa fargli. Su un host
 * di distribuzione la risposta è quasi sempre la stessa per tutte e ottanta:
 * aggiornare i pacchetti. Una riga alla volta non si vede; tutte insieme è **un
 * comando solo**.
 *
 * È la regola che governa le altre — «il rimedio dev'essere una cosa sola, o
 * non si scrive» — applicata anche dove il rimedio è una cosa sola e non lo si
 * diceva.
 *
 * ## Perché il raggruppamento sta qui e non nel motore
 *
 * Perché il motore vede un asset alla volta e un reperto alla volta: mettere
 * insieme i pacchetti di ottanta reperti lo può fare solo chi li ha davanti
 * tutti. **Quale** comando sia lo decide il motore, che è l'unico a sapere di
 * che distribuzione si parla; qui si concatena e basta.
 */
import type { SecurityFinding } from '../api/types'

/** Un'azione: una cosa da fare, e quanti reperti chiude. */
export interface Azione {
  /**
   * Cosa fare, come **modello con segnaposto**.
   *
   * Non una frase già composta: una frase composta a runtime non esiste nel
   * sorgente, quindi non è una chiave di traduzione e nessuna guardia la vede.
   * In una console inglese resterebbe in italiano, e nessuno se ne
   * accorgerebbe finché non la guarda un cliente.
   */
  cosa: string
  /** I valori dei segnaposto di `cosa`. */
  valori?: Record<string, unknown>
  /** Il comando vero e proprio, quando esiste. Da mostrare in monospazio. */
  comando?: string
  /** Quanti reperti chiude. */
  chiude: number
  /** La gravità peggiore fra quelli che chiude: decide l'ordine. */
  gravita: string
  /**
   * Vero quando chiude vulnerabilità **già sfruttate attivamente (CISA KEV)**.
   *
   * Non è un sinonimo di «critica»: critica dice quanto sarebbe grave, questo
   * dice che qualcuno la sta usando. Va prima di tutto il resto, e va detto —
   * altrimenti si legge come una delle altre righe critiche.
   */
  urgente?: boolean
}

const PESO: Record<string, number> = { critica: 0, alta: 1, media: 2, bassa: 3 }

function peggiore(a: string, b: string): string {
  return (PESO[a] ?? 9) <= (PESO[b] ?? 9) ? a : b
}

/**
 * Le azioni che chiudono questi reperti, dalla più urgente.
 *
 * I reperti che si chiudono aggiornando finiscono in **un'azione sola** per
 * comando: è la riga che vale ottanta righe. Gli altri — un sistema fuori
 * supporto, una porta in chiaro — restano separati, perché il loro rimedio è
 * diverso e non si può accorpare.
 */
export function azioniDa(righe: SecurityFinding[]): Azione[] {
  // Per comando: i pacchetti da aggiornare, senza ripetizioni.
  const perComando = new Map<
    string,
    {
      pacchetti: Set<string>
      chiude: number
      gravita: string
      sfruttata: boolean
      comando: string
    }
  >()
  // Per rimedio scritto: i reperti che hanno una loro cosa da fare.
  const perRimedio = new Map<string, { chiude: number; gravita: string }>()
  // Quelli senza né l'uno né l'altro: si dicono, non si nascondono.
  let senzaRimedio = 0
  let gravitaSenza = 'bassa'

  for (const r of righe) {
    const comando = typeof r.evidence?.comando === 'string' ? r.evidence.comando : ''
    const pacchetti = Array.isArray(r.evidence?.aggiorna)
      ? (r.evidence.aggiorna as unknown[]).filter((x): x is string => typeof x === 'string')
      : []

    if (comando && pacchetti.length > 0) {
      // Le vulnerabilità **sfruttate attivamente (CISA KEV)** fanno azione a sé, anche se il
      // comando è lo stesso.
      //
      // Mescolate con le altre sparirebbero dentro «aggiorna 34 pacchetti,
      // chiude 78 reperti»: chi legge farebbe la stessa cosa, prima o poi, e
      // «prima o poi» è esattamente ciò che non va bene per una vulnerabilità
      // che qualcuno sta già usando. Separate, la prima riga della pagina dice
      // cosa fare stanotte.
      const sfruttata = r.evidence?.sfruttata === true
      const chiave = sfruttata ? `!${comando}` : comando
      const voce = perComando.get(chiave) ?? {
        pacchetti: new Set<string>(),
        chiude: 0,
        gravita: 'bassa',
        sfruttata,
        comando,
      }
      pacchetti.forEach((p) => voce.pacchetti.add(p))
      voce.chiude += 1
      voce.gravita = peggiore(voce.gravita, r.severity)
      perComando.set(chiave, voce)
      continue
    }

    if (r.remedy) {
      const voce = perRimedio.get(r.remedy) ?? { chiude: 0, gravita: 'bassa' }
      voce.chiude += 1
      voce.gravita = peggiore(voce.gravita, r.severity)
      perRimedio.set(r.remedy, voce)
      continue
    }

    senzaRimedio += 1
    gravitaSenza = peggiore(gravitaSenza, r.severity)
  }

  const azioni: Azione[] = []

  for (const voce of perComando.values()) {
    const pacchetti = [...voce.pacchetti].sort()
    azioni.push({
      cosa: voce.sfruttata
        ? 'Aggiorna {n} pacchetti con vulnerabilità già sfruttate'
        : 'Aggiorna {n} pacchetti',
      valori: { n: pacchetti.length },
      // I pacchetti **tutti**, non i primi: il comando si incolla, e un comando
      // troncato aggiorna meno di quanto la riga sopra promette. Chi lo legge
      // crederebbe di aver chiuso tutti i reperti che il conteggio dichiara.
      comando: `${voce.comando} ${pacchetti.join(' ')}`,
      chiude: voce.chiude,
      gravita: voce.gravita,
      urgente: voce.sfruttata,
    })
  }

  for (const [rimedio, voce] of perRimedio) {
    azioni.push({ cosa: rimedio, chiude: voce.chiude, gravita: voce.gravita })
  }

  if (senzaRimedio > 0) {
    // Non si tace: un reperto senza rimedio scritto è comunque un reperto, e
    // ometterlo dal riepilogo farebbe tornare i conti sbagliati — «tre azioni
    // chiudono tutto» quando non è vero.
    azioni.push({
      cosa: 'Da valutare a mano: non c’è un rimedio unico',
      chiude: senzaRimedio,
      gravita: gravitaSenza,
    })
  }

  // Prima la gravità peggiore, poi quante ne chiude: si comincia da ciò che
  // scotta, e a parità si fa prima ciò che rende di più.
  // Prima ciò che è **già sfruttato**, poi la gravità, poi quante ne chiude.
  //
  // L'urgenza prima della gravità perché rispondono a due domande diverse:
  // «quanto sarebbe grave» e «lo stanno già facendo». La seconda decide cosa si
  // fa stanotte.
  azioni.sort(
    (a, b) =>
      Number(b.urgente ?? false) - Number(a.urgente ?? false) ||
      (PESO[a.gravita] ?? 9) - (PESO[b.gravita] ?? 9) ||
      b.chiude - a.chiude,
  )
  return azioni
}
